import styles from "./Templates.module.scss";
import {
  canAddActivitiesToPatient,
  useNewActivityOutletContext,
} from "@/pages/commandcenter/NewActivity/newActivityUtils";
import { Trans, useLingui } from "@lingui/react/macro";
import { usePatient } from "@/api/Patients";
import { Text } from "@medoma/components/Text/Text";
import { deducedError } from "@/Utils/ErrorUtils";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { useTemplatesContext } from "./TemplatesContext";
import { TemplatesContextProvider } from "./TemplatesContextProvider";
import ControlledInput from "@medoma/components/InputField/ControlledInput";
import { useState } from "react";
import {
  templatesQueryOptions,
  tagsByGroupQueryOptions,
  type IActivityTemplate,
  type IBatchTemplate,
} from "@/api/Templates";
import { useQuery } from "@tanstack/react-query";
import { BatchTemplate } from "./BatchTemplate";
import { ActivityTemplate } from "./ActivityTemplate";
import { isBatchTemplate, isActivityTemplate } from "@medoma/models/templates";
import { groupTemplatesByTag } from "@/pages/template-manager/tagUtils";
import { Heading } from "@medoma/components/Heading/Heading";
import { useMatchPatientTypes } from "./templateUtils";
import { CompetenceFilter } from "@/pages/template-manager/CompetenceFilter";
import { useTemplateManagerCompetenceFilter } from "@/pages/template-manager/useTemplateManagerCompetenceFilter";
import {
  sortActivityTemplates,
  sortBatchTemplates,
} from "@/pages/template-manager/templateManagerUtils";
import NoResults from "@/components/NoResults/NoResults";
import { TemplatesWithoutPatient } from "./TemplatesWithoutPatient";

export const Templates = () => {
  const { patientId, defaultStartDate } = useNewActivityOutletContext();
  const { t } = useLingui();
  const {
    data: templates,
    isPending,
    isError,
    error,
  } = useQuery(templatesQueryOptions());

  if (isPending) {
    return <Loading message={t`Hämtar mallar`} />;
  }

  if (isError) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta mallar`} ${deducedError(error)}`}
      />
    );
  }

  const activityTemplatesWithoutPatient = templates.activities.withoutPatient;

  if (!patientId) {
    if (activityTemplatesWithoutPatient.length > 0) {
      return (
        <>
          <TemplatesWithoutPatient
            defaultStartDate={defaultStartDate}
            activityTemplatesWithoutPatient={activityTemplatesWithoutPatient}
          />
          <div className={styles.otherTemplatesMessage}>
            <Text element="p" color="no-results">
              <Trans>Välj en patient för att visa övriga mallar</Trans>
            </Text>
          </div>
        </>
      );
    } else {
      return (
        <Text element="p" color="no-results">
          <Trans>Välj en patient för att visa tillgängliga mallar</Trans>
        </Text>
      );
    }
  }

  return (
    <TemplatesContextProvider
      initialPatientId={patientId}
      initialDefaultStartDate={defaultStartDate}
    >
      <TemplatesFromTemplateManager />
    </TemplatesContextProvider>
  );
};

const BatchTemplateList = ({ batchList }: { batchList: IBatchTemplate[] }) => (
  <>
    {sortBatchTemplates([...batchList]).map((batch) => (
      <li key={batch.templateId}>
        <BatchTemplate template={batch} />
      </li>
    ))}
  </>
);

const ActivityTemplateList = ({
  activityList,
}: {
  activityList: IActivityTemplate[];
}) => (
  <>
    {sortActivityTemplates([...activityList]).map((activity) => (
      <li key={activity.templateId}>
        <ActivityTemplate template={activity} />
      </li>
    ))}
  </>
);

const TemplatesFromTemplateManager = () => {
  const { t } = useLingui();
  const { selectedPatientId: patientId } = useTemplatesContext();
  const {
    data: patient,
    isPending: isPatientPending,
    isError: isPatientError,
    error: patientError,
  } = usePatient(patientId);
  const {
    data: templates,
    isPending: isTemplatesPending,
    isError: isTemplatesError,
    error: templatesError,
  } = useQuery(templatesQueryOptions());
  const {
    data: groupTags,
    isPending: isGroupTagsPending,
    isError: isGroupTagsError,
    error: groupTagsError,
  } = useQuery(tagsByGroupQueryOptions("group"));
  const { isPatientTypesMatching } = useMatchPatientTypes(patientId);

  const { filteredCompetence, selectCompetence, matchesCompetenceFilter } =
    useTemplateManagerCompetenceFilter("templates.competenceFilter");
  const [titleSearchValue, setTitleSearchValue] = useState("");
  const normalizedTitleFilter = titleSearchValue.trim().toLocaleLowerCase();
  const matchesTitleFilter = (title: string) =>
    title.toLocaleLowerCase().includes(normalizedTitleFilter);

  if (isPatientPending || isTemplatesPending || isGroupTagsPending) {
    return <Loading message={t`Hämtar mallar`} />;
  }

  if (isPatientError || isTemplatesError || isGroupTagsError) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta mallar`} ${deducedError(patientError || templatesError || groupTagsError)}`}
      />
    );
  }

  const { activities, batches } = templates;
  const activitiesWithPatient = activities.withPatient;

  if (!canAddActivitiesToPatient(patient)) {
    return (
      <Text element="p" color="faded">
        <Trans>
          Patienten har fått avslag eller arkiverats. Mallar kan inte användas.
        </Trans>
      </Text>
    );
  }

  const filteredActivityTemplates = activitiesWithPatient.filter((template) => {
    return (
      isPatientTypesMatching([template.template.patientType]) &&
      matchesCompetenceFilter(template) &&
      matchesTitleFilter(template.template.title)
    );
  });

  const filteredBatchTemplates = batches.filter((template) => {
    const batchMatchesTitle =
      matchesTitleFilter(template.template.title) ||
      template.template.activities.some((activity) =>
        matchesTitleFilter(activity.title),
      );
    return (
      isPatientTypesMatching([template.template.patientType]) &&
      matchesCompetenceFilter(template) &&
      batchMatchesTitle
    );
  });

  const { groups, ungroupedTemplates } = groupTemplatesByTag(
    [...filteredBatchTemplates, ...filteredActivityTemplates],
    groupTags,
  );

  const nonEmptyGroupsWithTemplates = groups
    .filter((group) => group.templates.length > 0)
    .map((group) => {
      return {
        groupId: group.groupId,
        groupName: group.groupName,
        batches: group.templates.filter(isBatchTemplate),
        activities: group.templates.filter(isActivityTemplate),
      };
    });

  const ungroupedBatches = ungroupedTemplates.filter(isBatchTemplate);
  const ungroupedActivities = ungroupedTemplates.filter(isActivityTemplate);
  const hasUngroupedTemplates =
    ungroupedBatches.length > 0 || ungroupedActivities.length > 0;
  const hasNoMatchingTemplates =
    nonEmptyGroupsWithTemplates.length === 0 && !hasUngroupedTemplates;

  return (
    <div className={styles.templatesContainer}>
      <CompetenceFilter
        filteredCompetence={filteredCompetence}
        selectCompetence={selectCompetence}
      />
      <search className={styles.search}>
        <ControlledInput
          type="search"
          label={t`Sök mall`}
          size="compact"
          value={titleSearchValue}
          onChange={(e) => setTitleSearchValue(e.target.value)}
        />
      </search>
      {nonEmptyGroupsWithTemplates.map((group) => {
        return (
          <section key={group.groupId} className={styles.groupSection}>
            <Heading level="h3" weight="medium">
              {group.groupName}
            </Heading>
            {group.batches.length > 0 ? (
              <ul className={styles.listGroup}>
                <BatchTemplateList batchList={group.batches} />
              </ul>
            ) : null}
            {group.activities.length > 0 ? (
              <ul className={styles.listGroup}>
                <ActivityTemplateList activityList={group.activities} />
              </ul>
            ) : null}
          </section>
        );
      })}
      {hasUngroupedTemplates ? (
        <>
          {ungroupedBatches.length > 0 ? (
            <ul className={styles.listGroup}>
              <BatchTemplateList batchList={ungroupedBatches} />
            </ul>
          ) : null}
          {ungroupedActivities.length > 0 ? (
            <ul className={styles.listGroup}>
              <ActivityTemplateList activityList={ungroupedActivities} />
            </ul>
          ) : null}
        </>
      ) : null}
      {hasNoMatchingTemplates ? (
        <NoResults message={t`Inga mallar matchar din sökning`} />
      ) : null}
    </div>
  );
};
